import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Languages,
  List,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { frenchCurriculum } from '@/data/frenchCurriculum';
import { 
  year7FrenchUnitTests,
  year8FrenchUnitTests,
  year9FrenchUnitTests,
  year10FrenchUnitTests,
  year7FrenchExercises,
  year8FrenchExercises,
  year9FrenchExercises,
  year10FrenchExercises
} from '@/data/frenchUnitTests';
import { frenchWeeklyLessons } from '@/data/frenchWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { PenTool, Clock } from 'lucide-react';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function FrenchCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 7");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(c => c.year >= 7 && c.year <= 10 && 
            (c.subject?.toLowerCase().includes('french') || 
             c.subject?.toLowerCase().includes('fren')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 7", "Year 8", "Year 9", "Year 10"]);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears(["Year 7", "Year 8", "Year 9", "Year 10"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const strandColors = ["blue", "green", "purple"];

  // Get curriculum data for selected year
  const getCurriculumData = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    return frenchCurriculum[yearKey] || frenchCurriculum.year7;
  };

  // Get unit tests for selected year
  const getUnitTests = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const testsMap = {
      'year7': year7FrenchUnitTests,
      'year8': year8FrenchUnitTests,
      'year9': year9FrenchUnitTests,
      'year10': year10FrenchUnitTests
    };
    return testsMap[yearKey] || year7FrenchUnitTests;
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getUnitTests();
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests?.[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  // Get exercises for selected year
  const getExercises = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const exercisesMap = {
      'year7': year7FrenchExercises,
      'year8': year8FrenchExercises,
      'year9': year9FrenchExercises,
      'year10': year10FrenchExercises
    };
    return exercisesMap[yearKey] || year7FrenchExercises;
  };

  const curriculumData = getCurriculumData();
  const unitTests = getUnitTests();
  const exercises = getExercises();

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #0284c7; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #0284c7; background: #f0f9ff; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #0284c7; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .question { margin-bottom: 15px; padding: 10px; border-bottom: 1px solid #eee; }
          .q-number { font-weight: bold; color: #0284c7; }
          .marks { float: right; color: #666; font-style: italic; }
          .answer-lines { margin-top: 10px; }
          .answer-line { border-bottom: 1px solid #ccc; height: 25px; margin: 8px 0; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} French</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            ${section.questions ? `<p>${section.questions} questions</p>` : ''}
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-sky-600 to-cyan-500 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Languages className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">French Curriculum</CardTitle>
                <CardDescription className="text-sky-100 mt-1">
                  Junior Secondary (Years 7-10) - Vanuatu National Syllabus
                </CardDescription>
              </div>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-sky-100 text-sm">Year Level:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[140px] bg-white/20 border-white/30 text-white" data-testid="french-year-selector">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {teacherYears.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Course Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-sky-500" />
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold">40 Weeks/Year</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-500">Strands</p>
                <p className="font-semibold">{curriculumData?.strands?.length || 3} Strands</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Assessment</p>
                <p className="font-semibold">Oral & Written</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm text-gray-500">Level</p>
                <p className="font-semibold">Junior Secondary</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 m-4">
            <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
              <BookOpen className="h-4 w-4" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
              <Clock className="h-4 w-4" />
              Schemes
            </TabsTrigger>
            <TabsTrigger value="tests" className="flex items-center gap-2" data-testid="tests-tab">
              <ClipboardCheck className="h-4 w-4" />
              Unit Tests
            </TabsTrigger>
            <TabsTrigger value="exercises" className="flex items-center gap-2" data-testid="exercises-tab">
              <FileText className="h-4 w-4" />
              Exercises
            </TabsTrigger>
            <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
              <FileQuestion className="h-4 w-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
              <Files className="h-4 w-4" />
              Quick Print
            </TabsTrigger>
            <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-sky-100" data-testid="lesson-plans-tab">
              <PenTool className="h-4 w-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
              <Sparkles className="h-4 w-4" />
              AI Generator
            </TabsTrigger>
          </TabsList>

          {/* Schemes Tab - Weekly Scheme of Work */}
          <TabsContent value="schemes" className="p-4">
            <SubjectSchemeOfWork
              subject="French"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={frenchWeeklyLessons}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Term Exam Tab */}
          <TabsContent value="term-exam" className="p-4">
            <TermExamGenerator
              subject="French"
              year={parseInt(selectedYear.replace('Year ', ''))}
              unitTests={getUnitTestsForYear()}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Quick Print Tab */}
          <TabsContent value="quick-print" className="p-4">
            <QuickPrintAll
              subject="French"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={frenchWeeklyLessons}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Lesson Plans Tab - Full Week Mon-Fri */}
          <TabsContent value="lesson-plans" className="p-4">
            <LessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              subject="French"
              subjectColor="teal"
              allowedYears={['7', '8', '9', '10', '11', '12', '13']}
              weeklyLessons={frenchWeeklyLessons}
            />
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">{curriculumData?.title || `${selectedYear} French`}</h3>
              <p className="text-gray-500">{curriculumData?.description || 'French as a Foreign Language'}</p>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {curriculumData?.strands?.map((strand, index) => (
                <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg overflow-hidden">
                  <AccordionTrigger className={`px-4 py-3 bg-${strandColors[index % 3]}-50 hover:bg-${strandColors[index % 3]}-100`}>
                    <div className="flex items-center gap-3">
                      <Badge className={`bg-${strandColors[index % 3]}-500 text-white`}>
                        Strand {index + 1}
                      </Badge>
                      <span className="font-semibold">{strand.name}</span>
                      <span className="text-gray-500 text-sm ml-2">({strand.units?.length || 0} units)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4 bg-white">
                    <div className="space-y-4">
                      {strand.units?.map((unit, uIdx) => (
                        <div key={unit.id || uIdx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-800">{unit.name}</h4>
                            <div className="flex gap-2">
                              {unit.weeks && <Badge variant="outline" className="text-xs">Weeks {unit.weeks}</Badge>}
                              {unit.duration && <Badge variant="secondary" className="text-xs">{unit.duration}</Badge>}
                            </div>
                          </div>
                          {unit.topics && unit.topics.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-600 mb-1">Topics:</p>
                              <ul className="space-y-1">
                                {unit.topics.map((topic, tIdx) => (
                                  <li key={tIdx} className="flex items-start gap-2 text-sm text-gray-600">
                                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{topic}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {unit.objectives && unit.objectives.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-600 mb-1">Learning Objectives:</p>
                              <ul className="space-y-1">
                                {unit.objectives.map((obj, oIdx) => (
                                  <li key={oIdx} className="flex items-start gap-2 text-sm text-gray-500">
                                    <Target className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                    <span>{obj}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>

          {/* Unit Tests Tab */}
          <TabsContent value="tests" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Unit Tests - {selectedYear}</h3>
              <p className="text-gray-500">Practice tests and assessments for each term</p>
            </div>
            
            {unitTests && Object.keys(unitTests).length > 0 ? (
              <Accordion type="multiple" className="space-y-3">
                {Object.entries(unitTests).map(([termKey, termData]) => (
                  <AccordionItem key={termKey} value={termKey} className="border rounded-lg">
                    <AccordionTrigger className="px-4 py-3 bg-sky-50 hover:bg-sky-100">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-sky-500 text-white">
                          {termData.title || termKey.replace('term', 'Term ')}
                        </Badge>
                        <span className="text-gray-600">
                          {termData.tests?.length || 0} Tests
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4">
                      <div className="space-y-3">
                        {termData.tests?.map((test, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <ClipboardCheck className="h-5 w-5 text-sky-500" />
                              <div>
                                <p className="font-medium">{test.title}</p>
                                <p className="text-sm text-gray-500">
                                  {test.duration} | {test.totalMarks} marks
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => printTest(test)}
                            >
                              <Printer className="h-4 w-4 mr-2" />
                              Print
                            </Button>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <p className="text-gray-500 italic">Unit tests are being developed for {selectedYear}.</p>
            )}
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Exercises - {selectedYear}</h3>
              <p className="text-gray-500">Practice worksheets and activities</p>
            </div>
            
            {exercises && Object.keys(exercises).length > 0 ? (
              <Accordion type="multiple" className="space-y-3">
                {Object.entries(exercises).map(([unitKey, unitData]) => (
                  <AccordionItem key={unitKey} value={unitKey} className="border rounded-lg">
                    <AccordionTrigger className="px-4 py-3 bg-green-50 hover:bg-green-100">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="font-medium">{unitData.title || unitKey.replace('unit', 'Unit ')}</span>
                        <Badge variant="outline">
                          {unitData.exercises?.length || 0} exercises
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4">
                      <div className="space-y-3">
                        {unitData.exercises?.map((exercise, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium">{exercise.title}</p>
                              <Badge>{exercise.type}</Badge>
                            </div>
                            <p className="text-sm text-gray-500">{exercise.instructions}</p>
                            {exercise.questions?.length > 0 && (
                              <div className="mt-2 text-sm">
                                <p className="text-gray-600">{exercise.questions.length} questions</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <p className="text-gray-500 italic">Exercises are being developed for {selectedYear}.</p>
            )}
          </TabsContent>

          {/* AI Lesson Plan Tab */}
          <TabsContent value="ai-lesson" className="p-4">
            <AILessonPlanGenerator 
              subject="French"
              yearLevel={selectedYear}
              strands={curriculumData?.strands?.map(s => s.name) || []}
            />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
